import {
  Button,
  Divider,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PlaylistAddRounded, UpdateRounded } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { RootState } from '../../../app/store';
import { useAppDispatch } from '../../../hooks/useAppRudux';
import Breadcrumb from '../../Breadcrumb';
import loadingSlice from '../../Loading/loadingSlice';
import {
  createWorkflowStatusAsync,
  getWorkflowStatusAsync,
  updateWorkflowStatusAsync
} from './workflowStatusSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    paper: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: '16px'
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: 32
    }
  })
);

interface CreateEditWorkflowStatus {
  id: number | null;
  name: string | null;
}

export default function WorkflowStatusCreateEdit() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { selectedWorkflowStatus } = useSelector(
    (state: RootState) => state.workflowStatuses
  );

  const params = useParams<{ workflowStatusId: string }>();
  const workflowStatusId = Number.parseInt(params.workflowStatusId, 10);
  const isNewPage = !Boolean(workflowStatusId);

  const [workflowStatus, setWorkflowStatus] =
    useState<CreateEditWorkflowStatus>({
      id: null,
      name: null
    });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isNewPage && selectedWorkflowStatus) {
      setWorkflowStatus({
        ...workflowStatus,
        id: selectedWorkflowStatus.id,
        name: selectedWorkflowStatus.name
      });
    }
  }, [selectedWorkflowStatus]);

  useEffect(() => {
    if (!isNewPage) dispatch(getWorkflowStatusAsync(workflowStatusId));
  }, []);

  function onChangeField(event: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setWorkflowStatus({
      ...workflowStatus,
      [name]: value === '' ? null : value
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (workflowStatus.name) {
        if (isNewPage) {
          // create workflow status
          const createWorkflowStatus = {
            name: workflowStatus.name
          };
          await dispatch(
            createWorkflowStatusAsync(createWorkflowStatus)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowStatusCreateEdit.messages.createSuccess')
            })
          );
        } else {
          // update workflow status
          const updateWorkflowStatus = {
            id: workflowStatus.id,
            name: workflowStatus.name
          };
          await dispatch(
            updateWorkflowStatusAsync(updateWorkflowStatus)
          ).unwrap();
          dispatch(
            loadingSlice.actions.success({
              message: t('workflowStatusCreateEdit.messages.updateSuccess')
            })
          );
        }
      } else {
        throw new Error(t('workflowStatusCreateEdit.errors.general') as string);
      }
      setTimeout(() => {
        history.goBack();
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name={isNewPage ? 'masterDataCreate' : 'masterDataSubEdit'}
        data={{
          masterDataName: t('workflowStatusCreateEdit.breadcrumb.masterDataName'),
          masterDataRef: 'workflow-statuses',
          masterDataSubName: selectedWorkflowStatus?.name,
          masterDataSubRef: selectedWorkflowStatus?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {isNewPage ? t('workflowStatusCreateEdit.title.new') : t('workflowStatusCreateEdit.title.edit')}
      </Typography>
      <form onSubmit={onSubmit}>
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {t('workflowStatusCreateEdit.sections.information')}
          </Typography>
          <Divider className={classes.divider} />
          <TextField
            id="workflow-status-name"
            label={t('workflowStatusCreateEdit.labels.name')}
            size="small"
            variant="outlined"
            name="name"
            fullWidth
            value={workflowStatus.name ? workflowStatus.name : ''}
            onChange={onChangeField}
            required
          />
        </Paper>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={isNewPage ? <PlaylistAddRounded /> : <UpdateRounded />}
          disabled={loading}
        >
          {isNewPage ? t('workflowStatusCreateEdit.buttons.add') : t('workflowStatusCreateEdit.buttons.update')}
        </Button>
      </form>
    </div>
  );
}
